import { Card } from '@guridosdrones/ui';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import { useState } from 'react';
import { FadeIn, StaggerContainer, StaggerItem } from '@/components';
import { getWhatsAppLink, WHATSAPP_MESSAGES } from '@/utils/whatsapp';

const faqData = [
  {
    question: 'Para quem é o curso Guri dos Drones?',
    answer:
      'Esse treinamento foi criado para produtores rurais, engenheiros agrônomos e prestadores de serviços que querem parar de perder tempo com mapeamentos manuais. Ensinamos a utilizar drones de imagem para automatizar o mapeamento de pulverização de forma rápida, fácil e extremamente precisa.',
  },
  {
    question: 'Quais modelos de drones podem ser usados?',
    answer:
      'O método é compatível com os principais drones de pulverização do mercado, incluindo as linhas DJI Agras (T10, T25, T40, T50) e XAG (V40, V50, P100, P100 PRO, P150, P60).',
  },
  {
    question: 'Eu preciso ter um drone de imagem específico?',
    answer:
      'Para a coleta de imagens, você precisará de um drone com uma boa câmera e GPS. No curso, ensinamos como coletar as fotos necessárias mesmo em modelos que não possuem aplicativos nativos de mapeamento.',
  },
  {
    question: 'O mapeamento feito com drone de imagem é preciso?',
    answer:
      'Sim! Mesmo que a imagem do drone apresente algum deslocamento natural, ensinamos a utilizar pontos de calibração para corrigir o mapa via RTK (VRTK), garantindo aplicação cirúrgica no campo.',
  },
  {
    question: 'Como funciona o Certificado de Conclusão?',
    answer:
      'Ao finalizar o curso, você poderá emitir seu certificado digital diretamente na plataforma. O certificado é reconhecido pelo mercado, pode ser compartilhado no LinkedIn e anexado ao seu currículo profissional.',
  },
  {
    question: 'Como receberei o acesso ao produto?',
    answer:
      'O acesso é imediato e enviado via e-mail após a confirmação do pagamento. Você poderá acessar as aulas por computador, celular ou tablet através da plataforma Hotmart.',
  },
  {
    question: 'Preciso comprar algum programa de mapeamento à parte?',
    answer:
      'Não. Dentro do curso, disponibilizamos todos os programas necessários e ensinamos passo a passo como baixá-los e configurá-los sem custos adicionais.',
  },
  {
    question: 'Como funciona o Prazo de Garantia?',
    answer:
      'Oferecemos uma garantia incondicional de 7 dias. Se por qualquer motivo você achar que o curso não é para você, basta solicitar o reembolso integral dentro desse período, sem burocracia.',
  },
];

interface FAQItemProps {
  question: string;
  answer: string;
  index: number;
  isOpen: boolean;
  onClick: () => void;
}

function FAQItem({ question, answer, index, isOpen, onClick }: FAQItemProps) {
  return (
    <StaggerItem>
      <Card
        className={`overflow-hidden border transition-all duration-300 ${
          isOpen
            ? 'border-primary/40 shadow-md ring-1 ring-primary/15'
            : 'border-border hover:border-primary/25'
        } bg-card cursor-pointer`}
        onClick={onClick}
      >
        <div className="p-5 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <span
              className={`font-mono text-[10px] tracking-[0.2em] transition-colors ${
                isOpen ? 'text-primary' : 'text-muted-foreground/50'
              }`}
            >
              {String(index + 1).padStart(2, '0')}
            </span>
            <h3
              className={`text-base font-bold transition-colors sm:text-lg ${isOpen ? 'text-primary' : 'text-foreground'}`}
            >
              {question}
            </h3>
          </div>
          <ChevronDown
            className={`w-5 h-5 shrink-0 text-muted-foreground transition-transform duration-300 ${
              isOpen ? 'rotate-180 text-primary' : ''
            }`}
          />
        </div>
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3, ease: 'easeInOut' }}
            >
              <div className="px-5 pb-6 pt-2 border-t border-border/50">
                <p className="pl-9 text-base leading-relaxed whitespace-pre-line text-muted-foreground">
                  {answer}
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </Card>
    </StaggerItem>
  );
}

export function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  return (
    <section id="faq" className="section-shell bg-muted/20">
      {/* Top line */}
      <div className="absolute top-0 left-0 w-full h-px bg-linear-to-r from-transparent via-primary/15 to-transparent" />

      <div className="section-container">
        <div className="section-header">
          <FadeIn direction="up" distance={20}>
            <p className="mb-3 font-mono text-xs tracking-[0.25em] text-primary/70 uppercase">
              Base de Dados
            </p>
            <h2 className="section-title mb-4">Dúvidas Frequentes</h2>
            <p className="section-description">
              Tudo o que você precisa saber para começar sua jornada com segurança e transparência.
            </p>
          </FadeIn>
        </div>

        <StaggerContainer className="w-full grid gap-4" staggerDelay={0.06}>
          {faqData.map((item, index) => (
            <FAQItem
              key={item.question}
              question={item.question}
              answer={item.answer}
              index={index}
              isOpen={openIndex === index}
              onClick={() => setOpenIndex(openIndex === index ? null : index)}
            />
          ))}
        </StaggerContainer>

        <FadeIn direction="none" delay={0.15} className="mt-14 text-center sm:mt-16">
          <p className="text-muted-foreground">
            Ainda tem dúvidas?{' '}
            <button
              type="button"
              onClick={() => window.open(getWhatsAppLink(WHATSAPP_MESSAGES.FAQ), '_blank')}
              className="text-primary font-bold hover:underline"
            >
              Fale com nosso suporte no WhatsApp
            </button>
          </p>
        </FadeIn>
      </div>
    </section>
  );
}
